import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TrackByFunction } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import * as moment from 'moment';
import { selectExtendedMessagesByConversationId } from 'common/store/genesys-cloud/conversations/messages/selectors/message.selectors';
import {
  ConversationContentAttachment,
  MessageData
} from 'common/store/genesys-cloud/conversations/messages/models/message-data.model';
import { ExtendedMessageData } from 'common/store/genesys-cloud/conversations/messages/models/extended-message-data.model';
import { selectMessageConversationById } from 'common/store/genesys-cloud/conversations/messages/selectors/message-conversation.selectors';
import { selectAttachments } from 'common/store/genesys-cloud/conversations/messages/selectors/message-attachment.selectors';
import { MessageDataSenderType } from 'common/store/genesys-cloud/conversations/common/models/message-data-sender-type.enum';
import { Contact } from 'common/store/genesys-cloud/external-contacts/models/contact.model';

@Component({
  selector: 'app-genesys-chat-message-list',
  templateUrl: './genesys-chat-message-list.component.html',
  styleUrls: ['./genesys-chat-message-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysChatMessageListComponent {
  private readonly agentGroup = new Set<MessageDataSenderType>([
    MessageDataSenderType.ActualAgent,
    MessageDataSenderType.Bot,
    MessageDataSenderType.OtherAgent
  ]);
  private readonly conversationIdSubject = new ReplaySubject<string>(1);
  private readonly _conversation$ = this.conversationIdSubject.pipe(
    mergeMap((conversationId) => this.store.select(selectMessageConversationById(conversationId)))
  );

  readonly conversationId$ = this.conversationIdSubject.asObservable();
  readonly messages$ = this.conversationIdSubject.pipe(
    mergeMap((conversationId) => this.store.select(selectExtendedMessagesByConversationId(conversationId)))
  );
  readonly messageType$ = this.messages$.pipe(map((m) => m[0]?.messengerType));

  readonly isCustomerTyping$ = this._conversation$.pipe(map((conversation) => conversation.isCustomerTyping));

  readonly conversationStarted$ = this._conversation$.pipe(
    map((conversation) => conversation.startTime),
    distinctUntilChanged(),
    map((startTime) => moment(startTime).format('llll'))
  );
  readonly messageTrackByFn: TrackByFunction<MessageData> = (_, message) => message.id;

  @Input()
  contact: Contact;

  @Input()
  set conversationId(newValue: string) {
    this.conversationIdSubject.next(newValue);
  }
  @Input()
  isMessagingPeriodEnded: boolean;
  @Output()
  imageLoad = new EventEmitter<void>();

  constructor(private readonly store: Store) {}

  isFirstSegmentMessage(messages: ExtendedMessageData[], idx: number) {
    if (idx < 1) {
      return true;
    }
    const actual = messages[idx];
    const previous = messages[idx - 1];
    return !(this.areInAgentGroup(actual, previous) || actual.senderType === previous.senderType);
  }

  isLastSegmentMessage(messages: ExtendedMessageData[], idx: number) {
    if (idx >= messages.length - 1) {
      return true;
    }
    const actual = messages[idx];
    const next = messages[idx + 1];
    return actual.senderType !== next.senderType;
  }

  getAttachments$(message: ExtendedMessageData): Observable<ConversationContentAttachment[]> {
    return this.store.select(selectAttachments(message.conversationId, message.id));
  }

  areInAgentGroup(firstMessage: ExtendedMessageData, secondMessage: ExtendedMessageData): boolean {
    return this.agentGroup.has(firstMessage.senderType) && this.agentGroup.has(secondMessage.senderType);
  }
}
